import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowDown, ArrowUp, Plus, RefreshCw, X } from 'lucide-react';
import { Button } from '../ui/button';

export default function MemberList({
  campaign, locked, busy, onSetDefault, onMove, onRemove, onAdd, onReplace,
}) {
  const members = campaign.members || [];
  const defaultId = campaign.defaultMenuId || '';

  return (
    <div className="bg-[#18181B] border border-[#27272A] rounded-lg p-4" data-testid="member-list">
      <h2 className="font-semibold mb-3">Menus</h2>

      <label className="flex items-center gap-3 px-3 py-2 rounded-md text-sm text-[#A1A1AA] cursor-pointer hover:bg-white/5">
        <input
          type="radio"
          name="campaign-default"
          className="accent-[#06C755]"
          checked={!defaultId}
          disabled={locked || busy}
          onChange={() => onSetDefault(null)}
          data-testid="member-default-radio-none"
        />
        Don't change the default
      </label>

      {members.length === 0 ? (
        <p className="text-sm text-[#A1A1AA] px-3 py-6 text-center">No menus yet. Add some to get started.</p>
      ) : (
        <div className="divide-y divide-[#27272A]">
          {members.map((m, i) => (
            <div key={m.id} className="flex flex-wrap items-center gap-3 px-3 py-3" data-testid="member-row">
              <input
                type="radio"
                name="campaign-default"
                className="accent-[#06C755]"
                title="Default menu"
                aria-label={`Make ${m.name} the default menu`}
                checked={defaultId === m.id}
                disabled={locked || busy}
                onChange={() => onSetDefault(m.id)}
                data-testid="member-default-radio"
              />
              {m.imageUrl ? (
                <img src={m.imageUrl} alt="" className="w-16 rounded border border-[#27272A] object-cover" />
              ) : (
                <div className="w-16 h-10 rounded border border-dashed border-[#3F3F46]" />
              )}
              <div className="flex-1 min-w-[10rem]">
                <Link to={`/edit/${m.id}`} className="text-sm font-medium hover:text-[#00E05E]">{m.name}</Link>
                {m.richMenuAliasId ? (
                  <p className="text-xs font-mono text-[#A1A1AA]">{m.richMenuAliasId}</p>
                ) : (
                  <p className="text-xs text-red-400">No alias — existing chat users won't reach this menu</p>
                )}
              </div>
              <span className="text-xs text-[#71717A] capitalize">{m.status}</span>
              <div className="flex items-center gap-1">
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Move up" disabled={locked || busy || i === 0} onClick={() => onMove(i, -1)} data-testid="member-up">
                  <ArrowUp className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Move down" disabled={locked || busy || i === members.length - 1} onClick={() => onMove(i, 1)} data-testid="member-down">
                  <ArrowDown className="w-4 h-4" />
                </Button>
                <Button variant="ghost" size="icon" className="h-8 w-8 text-red-400 hover:text-red-300" aria-label="Remove from campaign" disabled={locked || busy} onClick={() => onRemove(m.id)} data-testid="member-remove">
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mt-4">
        <Button variant="outline" size="sm" className="border-[#27272A]" disabled={locked} onClick={onAdd} data-testid="add-menus-button">
          <Plus className="w-4 h-4 mr-2" /> Add menus
        </Button>
        <Button variant="outline" size="sm" className="border-[#27272A]" disabled={locked} onClick={onReplace} data-testid="replace-live-button">
          <RefreshCw className="w-4 h-4 mr-2" /> Replace a live menu
        </Button>
      </div>
    </div>
  );
}
